/**
 * La deuda de datos del Overview, derivada de **las alertas** y no solo de la
 * fila héroe.
 *
 * Por qué existe este archivo. Las tres pantallas de Overview calculaban su
 * deuda con `sinDato(payload.heroMetrics)` / `vencidas(payload.heroMetrics)`, y
 * la fila héroe son cinco métricas. La API, en cambio, alerta sobre el universo
 * entero del rol: veintiuna para admin. Medido en la app corriendo, `/api/overview`
 * traía seis `metric_missing` (NPS, LTV, CAC, LTV : CAC, Payback, NRR) y un
 * `data_expired`, ninguna de ellas en `heroMetrics`, y la pantalla titulaba
 * «No hay deuda de datos / AL DÍA» mientras `/revenue` decía «6 de las 11 no se
 * pueden leer». La interfaz afirmaba lo contrario de lo que decían sus propios
 * datos.
 *
 * Se toman **las dos fuentes**, unidas por slug:
 *
 *  - las alertas `metric_missing` / `data_expired`, que son el veredicto del
 *    servidor sobre todo el universo del rol;
 *  - la fila héroe, que es lo que la pantalla ya está mostrando, para que la
 *    deuda no dependa de que el servidor haya emitido la alerta.
 *
 * La alerta dice **qué** falta; el `MetricValue` es lo que hace falta para
 * **dibujarlo** (nombre, proveniencia, unidad para el diálogo de carga). Se
 * resuelve primero contra la fila héroe y después contra el catálogo de
 * `/api/metrics`, que el tablero ya trae. Si ninguno lo conoce, el ítem se
 * muestra igual con el título de la alerta: perder una alerta por no tener con
 * qué decorarla es exactamente el defecto que esto arregla.
 */
import type { Alert, MetricValue } from '@ops/contracts'

import { entidadDeAlerta } from './frentes'

/** Un renglón de la deuda de datos. */
export interface ItemDeDeuda {
  /** Slug de la métrica. De la alerta sale de su `href`. */
  slug: string
  /** Nombre a mostrar. Del `MetricValue` si lo hay; si no, el título de la alerta. */
  nombre: string
  /**
   * El valor del período, cuando lo conocemos. `null` = la alerta existe pero
   * ni la fila héroe ni el catálogo trajeron la métrica: se muestra igual, sin
   * proveniencia inventada y sin ofrecer una carga que no podríamos completar.
   */
  metric: MetricValue | null
}

export interface DeudaDeDatos {
  /** Nadie cargó el número en el período. */
  sinDato: ItemDeDeuda[]
  /** Hay número, pero la carga tiene más de 30 días. */
  vencidas: ItemDeDeuda[]
}

/** Métricas de la lista con la carga vencida. Sirve igual fuera de la deuda. */
export function estaVencida(metric: MetricValue): boolean {
  return metric.valor !== null && metric.provenance.freshness === 'expired'
}

/**
 * @param alerts Las alertas del payload, tal cual las manda la API.
 * @param heroMetrics La fila héroe de la pantalla.
 * @param delCatalogo Resolutor slug → `MetricValue` del catálogo de métricas
 *   (`meta.valorDe`). Es no bloqueante: puede no haber llegado.
 */
export function deudaDeDatos(
  alerts: readonly Alert[],
  heroMetrics: readonly MetricValue[],
  delCatalogo: (slug: string) => MetricValue | undefined = () => undefined,
): DeudaDeDatos {
  const hero = new Map(heroMetrics.map((m) => [m.slug, m]))
  const buscar = (slug: string): MetricValue | null =>
    hero.get(slug) ?? delCatalogo(slug) ?? null

  const lista = (
    kind: Alert['kind'],
    deLaFilaHero: (m: MetricValue) => boolean,
  ): ItemDeDeuda[] => {
    const items: ItemDeDeuda[] = []
    const vistos = new Set<string>()

    // Primero las alertas, en el orden en que las mandó la API.
    for (const alert of alerts) {
      if (alert.kind !== kind) continue
      const { entityId } = entidadDeAlerta(alert)
      if (vistos.has(entityId)) continue
      vistos.add(entityId)
      const metric = buscar(entityId)
      items.push({
        slug: entityId,
        // El nombre del catálogo antes que el título de la alerta: "NPS" se lee
        // mejor que "Falta cargar NPS" bajo un rótulo que ya dice "Sin dato en
        // el período". Parsear el título sería atarse a una redacción del
        // servidor que no es parte del contrato.
        nombre: metric?.nombre ?? alert.titulo,
        metric,
      })
    }

    // Después lo que la fila héroe muestra y la alerta no cubrió. Sin esto, un
    // hueco del generador de alertas volvería a esconder deuda que está a la
    // vista en la misma pantalla.
    for (const metric of heroMetrics) {
      if (!deLaFilaHero(metric) || vistos.has(metric.slug)) continue
      vistos.add(metric.slug)
      items.push({ slug: metric.slug, nombre: metric.nombre, metric })
    }

    return items
  }

  return {
    sinDato: lista('metric_missing', (m) => m.valor === null),
    vencidas: lista('data_expired', estaVencida),
  }
}
